import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaEye, FaPrint, FaEdit, FaTrashAlt } from "react-icons/fa";
import Swal from "sweetalert2";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getData, deleteData } from "../services/FetchNodeservices";

export default function InvoiceTable() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  /* ================= FETCH ================= */
  const fetchInvoices = async () => {
    try {
      const res = await getData("api/invoice_master");
      const data = Array.isArray(res) ? res : res?.data || [];
      setInvoices(data);
    } catch (err) {
      console.error("Error fetching invoices:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  /* ================= DELETE ================= */
  const deleteInvoice = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Invoice?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });

    if (!confirm.isConfirmed) return;

    try {
      const res = await deleteData(`api/invoice_master/${id}`);
      if (res.success) {
        Swal.fire("Deleted!", "Invoice deleted successfully", "success");
        fetchInvoices();
      } else {
        Swal.fire("Error", res.message || "Delete failed", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Server error", "error");
    }
  };

  /* ================= PRINT ================= */
  const handlePrint = (id) => {
    const win = window.open(`/view-invoice/${id}?print=true`, "_blank");
    win?.addEventListener("load", () => setTimeout(() => win.print(), 700));
  };

  /* ================= PAGINATION ================= */
  const totalRows = invoices.length;
  const totalPages =
    rowsPerPage === "All" ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex =
    rowsPerPage === "All" ? 0 : (currentPage - 1) * rowsPerPage;
  const endIndex =
    rowsPerPage === "All" ? totalRows : startIndex + rowsPerPage;

  const currentRows =
    rowsPerPage === "All"
      ? invoices
      : invoices.slice(startIndex, endIndex);

  if (loading)
    return (
      <div className="flex justify-center items-center h-64">
        <span className="text-gray-500 animate-pulse text-lg">
          Loading invoices...
        </span>
      </div>
    );

  return (
    <>
      {/* <Header /> */}

      <div className="ml-0 md:ml-72 mt-16 p-6 bg-gray-100 min-h-screen">
        <div className="bg-white rounded-xl shadow-md p-6">

          {/* HEADER */}
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">
              Invoice Master
            </h2>

            <div className="flex items-center gap-2 text-sm">
              <label>Show</label>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(e.target.value === "All" ? "All" : Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border rounded-md px-2 py-1"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
                <option value="All">All</option>
              </select>
            </div>
          </div>

          {/* TABLE */}
          <div className="relative overflow-x-auto border rounded-lg">
           <table className="min-w-max w-full text-sm whitespace-nowrap">
              <thead className="bg-blue-600 text-white sticky top-0">
                <tr>
                  {[
                    "Invoice No", "Name", "GSTIN", "State", "Date",
                    "Taxable", "CGST", "SGST", "IGST",
                    "Total", "Adj.", "Due", "Actions"
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 whitespace-nowrap text-left">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {currentRows.length ? currentRows.map((row, i) => (
                 <tr key={row.id} className="border-b hover:bg-gray-50 whitespace-nowrap">
                    <td className="px-4 py-2 font-medium">{row.invoice_number}</td>
                    <td className="px-4 py-2">{row.name}</td>
                    <td className="px-4 py-2">{row.gstin}</td>
                    <td className="px-4 py-2">{row.state}</td>
                    <td className="px-4 py-2">
                      {new Date(row.invoice_date).toLocaleDateString("en-IN")}
                    </td>
                    <td className="px-4 py-2">₹{row.taxable_amount}</td>
                    <td className="px-4 py-2">₹{row.cgst}</td>
                    <td className="px-4 py-2">₹{row.sgst}</td>
                    <td className="px-4 py-2">₹{row.igst}</td>
                    <td className="px-4 py-2 font-semibold">₹{row.total_amount}</td>
                    <td className="px-4 py-2">₹{row.adjustment}</td>

                    {/* DUE PAYMENT COLUMN - Opens in New Tab */}
                    <td
                      className="px-4 py-2 text-center cursor-pointer group"
                      title="Click to manage payments"
                      onClick={() => window.open(`/invoice-payment/${row.id}`)}
                    >
                      <span
                        className={`px-3 py-1 rounded-full text-white text-xs font-bold transition-transform group-hover:scale-110 inline-block
                          ${row.amount_due == 0
                            ? "bg-green-600"
                            : row.amount_due < row.total_amount
                            ? "bg-orange-500"
                            : "bg-red-600"}`}
                      >
                        ₹{row.amount_due}
                      </span>
                    </td>

                    <td className="px-4 py-2 text-center space-x-2">
                      <IconBtn title="View" onClick={() => window.open(`/view-invoice/${row.id}`, "_blank")}>
                        <FaEye />
                      </IconBtn>
                      <IconBtn title="Print" onClick={() => handlePrint(row.id)}>
                        <FaPrint />
                      </IconBtn>
                      <IconBtn title="Edit" onClick={() => navigate(`/edit-invoice/${row.id}`)}>
                        <FaEdit />
                      </IconBtn>
                      <IconBtn title="Delete" danger onClick={() => deleteInvoice(row.id)}>
                        <FaTrashAlt />
                      </IconBtn>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="13" className="py-6 text-center text-gray-500">
                      No invoices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          {rowsPerPage !== "All" && totalPages > 1 && (
            <div className="flex justify-between items-center mt-4 text-sm">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(p => p - 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Prev
              </button>

              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-3 py-1 rounded border ${
                      currentPage === i + 1
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(p => p + 1)}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* <Footer /> */}
    </>
  );
}

function IconBtn({ children, onClick, title, danger }) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`p-1 ${
        danger ? "text-red-600" : "text-blue-600"
      } hover:scale-110 transition`}
    >
      {children}
    </button>
  );
}